import { useCallback, useContext, useEffect, useMemo } from 'react'

import { Button, Grid, Stack, Tooltip } from '@mui/material'
import { addBusinessDays } from 'date-fns'
import Decimal from 'decimal.js'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle, FplDynamicTabs, TabPanelBootstrap } from 'components/common'
import { CollectionInterval, IgnoredXeroServiceCodes } from 'constants/index'
import {
  BookingAddressType,
  GetDistanceCalculationQuery,
  GoogleDistanceStatus,
  useDeleteBookingAddressMutation,
  useGetDistanceCalculationLazyQuery,
} from 'generated/graphql'
import { isPageLoadingVar } from 'graphql/reactiveVariables'
import { getCutOffTimeInterval, getGoogleAddressInput, getReturnLegAddressInput } from 'helpers'
import usePrevious from 'helpers/usePrevious'
import { isReturnCollection } from 'helpers/waitAndReturn.helpers'
import { emitter, usePermission } from 'providers'
import { TBookingForm } from 'types'

import { AddressContext } from './AddressProvider'
import { BookingAddress, getBookingAddressDefaultValues } from './BookingAddress'

interface IProps {
  isQuote: boolean
  isQuickQuote: boolean
  isUpdate?: boolean
  hoverText: string
  disabled?: boolean
}

const CollectionDetails = (props: IProps) => {
  const { isQuote, isQuickQuote, isUpdate, hoverText, disabled } = props

  const { collectionTabValue, setCollectionTabValue, deliveryTabValue } = useContext(AddressContext)

  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<TBookingForm>()
  const { fields, append, remove } = useFieldArray({
    name: 'collectionAddresses',
    keyName: 'tabsKey',
  })

  const [isAllowedToUpdateBookingAfterQuoteIsBooked] = usePermission(
    'UpdateBookingAfterQuoteIsBooked',
  )
  const showAddAndRemoveButtons =
    !isQuote && !isAllowedToUpdateBookingAfterQuoteIsBooked ? false : true

  const isWaitAndReturn = useWatch({ name: 'isWaitAndReturn' })
  const collectionAddressesValue = useWatch({ name: 'collectionAddresses' })

  // Wait & Return is a there-and-back journey: no extra collections while it is ticked, and the
  // generated return collection is locked like the return delivery.
  const showAddButton = showAddAndRemoveButtons && !isWaitAndReturn
  const isReturnAddress = (addressIndex: number) =>
    isReturnCollection(collectionAddressesValue?.[addressIndex])

  // Queries
  const handleDistanceCalculation = useCallback(
    (data: GetDistanceCalculationQuery, distanceType: 'distance' | 'returnDistance') => {
      const { status, distance: rawDistance, errorMessage } = data.distanceCalculation

      if (status === GoogleDistanceStatus.Success) {
        const distance = new Decimal(rawDistance).toDP(2, Decimal.ROUND_FLOOR).toNumber()

        setValue(distanceType, distance, { shouldDirty: true, shouldValidate: true })
      } else if (status === GoogleDistanceStatus.Error) {
        toast.warning(errorMessage)
      }
    },
    [setValue],
  )

  const [getDistanceCalculation, { loading: distanceCalculationLoading }] =
    useGetDistanceCalculationLazyQuery({
      onError: (error) => {
        toast.error(error.message)
      },
    })

  // Mutations
  const [deleteBookingAddress] = useDeleteBookingAddressMutation()

  const fieldsLength = fields.length
  const prevFieldsLength = usePrevious<number>(fields.length)

  const collectionAddressesErrors = errors?.collectionAddresses

  const errorTabs = useMemo(
    () =>
      (collectionAddressesErrors as any)?.map((value, index) =>
        value !== undefined ? index : null,
      ),
    [collectionAddressesErrors],
  )

  // once an address is removed, recalculate the distance
  useEffect(() => {
    if (fieldsLength < Number(prevFieldsLength)) {
      const collectionAddressesValue = getValues('collectionAddresses')
      const deliveryAddressesValue = getValues('deliveryAddresses')
      const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

      if (collectionAddressesValue && deliveryAddressesValue) {
        const googleAddressInput = getGoogleAddressInput(
          collectionAddressesValue,
          deliveryAddressesValue,
        )

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        const returnLeg = isWaitAndReturn
          ? getReturnLegAddressInput(collectionAddressesValue, deliveryAddressesValue)
          : null

        if (returnLeg) {
          getDistanceCalculation({ variables: { input: returnLeg } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }, [
    fieldsLength,
    getDistanceCalculation,
    getValues,
    handleDistanceCalculation,
    prevFieldsLength,
    setValue,
  ])

  useEffect(() => {
    isPageLoadingVar(distanceCalculationLoading)
  }, [distanceCalculationLoading])

  useEffect(() => {
    emitter.on('xero-service-type-change', () => {
      const serviceType = getValues('serviceType')

      // check if service type is valid for default values
      if (!serviceType || IgnoredXeroServiceCodes.includes(serviceType)) {
        return
      }

      const collectionAddressesValue = getValues('collectionAddresses')
      const deliveryAddressesValue = getValues('deliveryAddresses')

      // is not multi-drop
      if (collectionAddressesValue.length + deliveryAddressesValue.length !== 2) {
        return
      }

      // al the cases are almost the same for each service type

      if (!collectionAddressesValue[0].at) {
        setValue(
          'collectionAddresses.0.at',
          addBusinessDays(CollectionInterval.start, getCutOffTimeInterval(serviceType)),
        )
      }

      if (!collectionAddressesValue[0].by) {
        setValue(
          'collectionAddresses.0.by',
          addBusinessDays(CollectionInterval.end, getCutOffTimeInterval(serviceType)),
        )
      }
    })

    return () => emitter.off('xero-service-type-change')
  }, [getValues, setValue])

  const handleTabChange = (_, newValue) => {
    setCollectionTabValue(newValue)
  }

  const handleAddClick = () => {
    const newTabValue = fields.length
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')
    const newSequenceOrder =
      [...collectionAddressesValue, ...deliveryAddressesValue].reduce(
        (max, address) => (address.sequenceOrder > max ? address.sequenceOrder : max),
        0,
      ) + 1

    append(getBookingAddressDefaultValues(newSequenceOrder))
    setCollectionTabValue(newTabValue)
  }

  const handleRemoveClick = (e, index: number, id?: string) => {
    e.stopPropagation()

    // Removing the generated return collection unticks Wait & Return, which removes both
    // generated stops through the Wait & Return effect.
    if (isReturnAddress(index)) {
      setValue('isWaitAndReturn', false, { shouldDirty: true })
      return
    }

    if (id) {
      deleteBookingAddress({ variables: { bookingAddressId: Number(id) } }).then((result) => {
        if (!result.data?.deleteBookingAddress) {
          return
        }

        if (collectionTabValue === fields.length - 1) {
          setCollectionTabValue(fields.length - 2)
        }
        remove(index)
        toast.success('Address was deleted with success')
      })
    } else {
      if (collectionTabValue === fields.length - 1) {
        setCollectionTabValue(fields.length - 2)
      }
      remove(index)
    }
  }

  const swapAddressValues = (sourceAddress, targetAddress, setValue) => {
    setValue(`${targetAddress}.name`, sourceAddress.name, { shouldDirty: true })
    setValue(`${targetAddress}.city`, sourceAddress.city, { shouldDirty: true })
    setValue(`${targetAddress}.addressLine1`, sourceAddress.addressLine1, { shouldDirty: true })
    setValue(`${targetAddress}.addressLine2`, sourceAddress.addressLine2, { shouldDirty: true })
    setValue(`${targetAddress}.postcode`, sourceAddress.postcode, { shouldDirty: true })
    setValue(`${targetAddress}.countryId`, sourceAddress.countryId, { shouldDirty: true })
    setValue(`${targetAddress}.contact`, sourceAddress.contact, { shouldDirty: true })
    setValue(`${targetAddress}.email`, sourceAddress.email, { shouldDirty: true })
    setValue(`${targetAddress}.telephoneNumber`, sourceAddress.telephoneNumber, {
      shouldDirty: true,
    })
    setValue(`${targetAddress}.isVerified`, sourceAddress.isVerified, { shouldDirty: true })
    setValue(`${targetAddress}.note`, sourceAddress.note, { shouldDirty: true })
  }

  const handleFlipAddresses = () => {
    const collectionAddress = { ...getValues(`collectionAddresses.${collectionTabValue}`) }
    const deliveryAddress = { ...getValues(`deliveryAddresses.${deliveryTabValue}`) }

    swapAddressValues(collectionAddress, `deliveryAddresses.${deliveryTabValue}`, setValue)

    swapAddressValues(deliveryAddress, `collectionAddresses.${collectionTabValue}`, setValue)
  }

  const handleFindOrAf = () => {
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')

    if (collectionAddressesValue && deliveryAddressesValue) {
      const googleAddressInput = getGoogleAddressInput(
        collectionAddressesValue,
        deliveryAddressesValue,
      )

      if (googleAddressInput.length >= 2) {
        const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )
        const returnLeg = isWaitAndReturn
          ? getReturnLegAddressInput(collectionAddressesValue, deliveryAddressesValue)
          : null

        if (returnLeg) {
          getDistanceCalculation({ variables: { input: returnLeg } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }

  return (
    <Stack spacing={1.5}>
      <Grid container columnSpacing={1.5} justifyContent='space-between'>
        <Grid item>
          <CardTitle marginBottom={0} disabled={disabled}>
            Collection
          </CardTitle>
        </Grid>

        <Grid item ml='auto'>
          <Tooltip
            title={
              isWaitAndReturn
                ? 'Addresses cannot be flipped while Wait & Return is ticked. Untick it first.'
                : ''
            }>
            <span>
              <Button
                variant='contained'
                color='grey'
                disabled={disabled || isWaitAndReturn}
                onClick={handleFlipAddresses}>
                Flip Addresses
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <Stack>
        <FplDynamicTabs
          tabValue={collectionTabValue}
          fields={fields}
          name='collection-address'
          label='Add'
          disabled={disabled}
          showAddButton={showAddButton}
          showRemoveButton={showAddAndRemoveButtons}
          errorTabs={errorTabs}
          onTabChange={handleTabChange}
          onAddClick={handleAddClick}
          onRemoveClick={handleRemoveClick}
        />

        <Tooltip title={disabled ? hoverText : ''} followCursor>
          <div>
            {fields.map((item, index) => (
              <TabPanelBootstrap value={collectionTabValue} index={index} key={item.tabsKey}>
                <BookingAddress
                  bookingAddressType={BookingAddressType.Collection}
                  disabled={disabled}
                  disableAllExceptAtBy={isReturnAddress(index)}
                  isQuote={isQuote}
                  isQuickQuote={isQuickQuote}
                  isUpdate={isUpdate}
                  item={item}
                  index={index}
                  key={item.tabsKey}
                  onFindOrAf={handleFindOrAf}
                />
              </TabPanelBootstrap>
            ))}
          </div>
        </Tooltip>
      </Stack>
    </Stack>
  )
}

export default CollectionDetails
